import { auth } from '@/store/db';
import store from '@/store';

/**
 * API Service for communicating with custom REST backend
 * Handles Firebase JWT authentication and HTTP requests
 */

// Configure your backend API base URL
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
}

export class ApiException extends Error {
  public status?: number;
  public code?: string;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiException';
    this.status = error.status;
    this.code = error.code;
  }
}

/**
 * Get current Firebase user's ID token
 * Automatically refreshes if needed
 */
async function getAuthToken(): Promise<string> {
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    throw new ApiException({
      message: 'User not authenticated',
      code: 'auth/not-authenticated',
      status: 401
    });
  }

  try {
    // Force refresh if token is older than 55 minutes (tokens expire at 60 min)
    const token = await currentUser.getIdToken(false);
    return token;
  } catch (error) {
    throw new ApiException({
      message: 'Failed to get authentication token',
      code: 'auth/token-error',
      status: 401
    });
  }
}

/**
 * Make an authenticated HTTP request to the API
 */
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = await getAuthToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers,
  };

  const url = `${API_BASE_URL}${endpoint}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle non-2xx responses
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorCode = `http/${response.status}`;

      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
        if (errorData.code) {
          errorCode = errorData.code;
        }
      } catch {
        // If error response isn't JSON, use default message
      }

      throw new ApiException({
        message: errorMessage,
        status: response.status,
        code: errorCode,
      });
    }

    // Handle empty responses (e.g., 204 No Content)
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      return {} as T;
    }

    const data = await response.json();
    if(data._MUTATE) {
      // automagically run mutations returned from the API
      for(const [type, payload] of Object.entries(data._MUTATE)) {
        console.log('Auto committing mutation from API response:', type, payload);
        store.commit(type, payload);
      }
    }

    delete data._MUTATE;

    return data as T;
  } catch (error) {
    if (error instanceof ApiException) {
      throw error;
    }

    // Network errors or other fetch failures
    throw new ApiException({
      message: error instanceof Error ? error.message : 'Network request failed',
      code: 'network/error',
    });
  }
}

/**
 * API Service - HTTP methods
 */
export const api = {
  /**
   * GET request
   */
  get: <T>(endpoint: string, params?: Record<string, unknown>): Promise<T> => {
    const queryString = params 
      ? '?' + new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== null)
            .map(([k, v]) => [k, String(v)])
        ).toString()
      : '';
    
    return request<T>(`${endpoint}${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * POST request
   */
  post: <T>(endpoint: string, data?: unknown): Promise<T> => {
    return request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * PUT request
   */
  put: <T>(endpoint: string, data?: unknown): Promise<T> => {
    return request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * PATCH request
   */
  patch: <T>(endpoint: string, data?: unknown): Promise<T> => {
    return request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  },

  /**
   * DELETE request
   */
  delete: <T>(endpoint: string): Promise<T> => {
    return request<T>(endpoint, {
      method: 'DELETE',
    });
  },

  /**
   * Test API connection and authentication
   * Verifies that JWT token is valid and backend is reachable
   */
  test: (): Promise<{ message: string; userId: string }> => {
    return request<{ message: string; userId: string }>('/test', {
      method: 'GET',
    });
  },
};

// ============================================================================
// TYPE DEFINITIONS - API Response Types
// ============================================================================

export interface CityDescription {
  grid: number;
  isItalian: number;
  name: string;
  region: string;
}

export interface Profile {
  id: string;
  birthyear: number;
  country: string;
  points: number;
  degree: string;
  familyname: string;
  givenname: string;
  italianOriginCountry: string;
  italianOriginCity: CityDescription | null;
  languageContext: string[];
  mainLanguage: string[];
  otherLanguages: string[];
  residence: CityDescription | null;
  placeLanguage: string[];
  places: {
    country: string;
    region?: string;
  }[];
}

export interface Question {
  id: number;
  num: number;
  template: 'yesno' | 'multiplechoice';
  image: string;
  category: string;
  question_know: string;
  question_use: string;
  answers: {
    id: number;
    text: string;
  }[];
}

export interface Survey {
  id: string;
  title: string;
  questions: Question[];
  date: string; // ISO 8601 date string (instead of Firestore Timestamp)
}

export interface Word {
  id: number;
  text: string;
  used: 0|1;
  url: string;
}

export interface SurveyIndex {
  answered: string[]; // list of survey ids
  surveyInfoRead: boolean; // whether the user has read the survey info
  words: {
    [questionId: string]: {
      answers: Word[];
      visited: 0|1;
    };
  };
}

export interface AnswerMultipleChoice {
  used: string[]; // array of answer ids as strings
  known: string[]; // array of answer ids as strings
  personal: string[]; // array of answer texts
  personalUsed: string[]; // array of answer texts
}

export type AnswerYesNo = {
  result: 'yes' | 'no';
};

export type SurveyAnswer = AnswerMultipleChoice | AnswerYesNo;

export interface QuestionStats {
  options: Word[];
  use: {
    [wordId: string]: {
      [grid: string]: number; // grid -> count
    };
  };
}

// ============================================================================
// API ENDPOINTS - Typed methods for each resource
// ============================================================================

/**
 * Survey-related API endpoints
 */
export const surveysApi = {
  /**
   * Get surveys created before a specific date
   * @param beforeDate - ISO date string
   */
  getSurveys: (beforeDate: string): Promise<Survey[]> => {
    return api.get<Survey[]>('/surveys', { 
      before: beforeDate,
      orderBy: 'date',
      order: 'desc'
    });
  },

  /**
   * Get survey index for current user
   * @param userId - User ID
   */
  getSurveyIndex: (userId: string): Promise<SurveyIndex> => {
    return api.get<SurveyIndex>(`/users/${userId}/survey-index`);
  },

  /**
   * Mark survey info as read
   * @param userId - User ID
   */
  setSurveyInfoRead: (userId: string): Promise<void> => {
    return api.patch<void>(`/users/${userId}/survey-index`, {
      surveyInfoRead: true
    });
  },

  /**
   * Add word to visited words
   * @param userId - User ID
   * @param questionId - Question ID to mark as visited
   */
  visitQuestion: (userId: string, questionId: number): Promise<void> => {
    return api.post<void>(`/users/${userId}/survey-index/visited-questions`, {
      questionId
    });
  },

  /**
   * Submit survey answers
   * @param surveyId - Survey ID
   * @param userId - User ID
   * @param answers - Array of answers
   */
  submitSurveyAnswers: (surveyId: string, userId: string, answers: SurveyAnswer[]): Promise<void> => {
    return api.post<void>(`/surveys/${surveyId}/answers/${userId}`, {
      answers,
    });
  },

  /**
   * 
   */
  questionImage: (surveyId: string, filename: string): string => {
    return API_BASE_URL + `/surveys/${surveyId}/images/${filename}`;
  }
};

/**
 * Profile-related API endpoints
 */
export const profileApi = {
  /**
   * Get user's private profile
   * @param userId - User ID
   */
  getProfile: (userId: string): Promise<Profile> => {
    return api.get<Profile>(`/users/${userId}/profile`);
  },

  /**
   * Update user's private profile
   * @param userId - User ID
   * @param profile - Profile data to update
   */
  updateProfile: (userId: string, profile: Partial<Profile>): Promise<void> => {
    return api.patch<void>(`/users/${userId}/profile`, profile);
  },

};


export const mapApi = {
  /**
   * Get map data for a specific question
   * @param questionId - Question ID
   */
  getQuestionStats: (questionId: number): Promise<QuestionStats> => {
    return api.get<QuestionStats>(`/questions/${questionId}/stats`);
  },
};

// eslint-disable-next-line
(window as any).api = api; // For debugging purposes

export default api;
